/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-11-23 15:00:00
 *
 *******************************************************/
//@flow

import React, { useCallback, useEffect, useState } from 'react';
import { Icon, Button, Collapsible, RadioButton, TextField } from '@shopify/polaris';
import IconChecked from 'src/assets/images/quick-start-guide/icon.checked.svg';
import IconClose from 'src/assets/images/quick-start-guide/icon.close.svg';
import IconChat from 'src/assets/images/quick-start-guide/icon.chat.svg';
import { ChevronDownMinor, ChevronUpMinor } from '@shopify/polaris-icons';

export function GuideV2Item({
  title,
  i18n,
  icon,
  children,
  countPassed,
  totalStep,
  keyGuide,
  feature,
  isOpen,
  onSendFeedback = () => {},
  loading,
  closeThisForever,

  setFeatureOpen,
  setFeatureClickedClose
}) {
  const [open, setOpen] = useState(isOpen);
  const [isClickedClose, setIsClickedClose] = useState(false);
  const [isShowSurveyForm, setIsShowSurveyForm] = useState(false);
  const [usefulValue, setUsefulValue] = useState(null);
  const [content, setContent] = useState('');
  const textUseful = {
    1: 'This is useful',
    2: "This isn't useful"
  };

  const handleChangeContent = useCallback(newValue => setContent(newValue), []);
  const handleChangeOption = useCallback(usefulValue => setUsefulValue(usefulValue), []);
  const handleToggle = useCallback(
    newValue => {
      setOpen(newValue);
      if (newValue) {
        setFeatureOpen(keyGuide);
      } else {
        setFeatureOpen(null);
      }
    },
    [keyGuide, setFeatureOpen]
  );
  const onCloseGuide = useCallback(() => {
    const currentValueIsClickedClose = isClickedClose;
    if (currentValueIsClickedClose) {
      closeThisForever(feature);
    } else {
      setFeatureClickedClose(feature);
      setIsClickedClose(true);
    }
  }, [isClickedClose, closeThisForever, feature, setFeatureClickedClose]);

  const handleSubmitFeedback = useCallback(() => {
    onSendFeedback({
      comment: content,
      status: textUseful[usefulValue],
      guideName: feature
    });
  }, [content, usefulValue, feature, onSendFeedback, textUseful]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen, open]);

  const isShowClose = countPassed === totalStep;
  const isLoadingSendFeedback = loading[`sendingFeedback${feature}`];

  return (
    <div className="GuideV2Item">
      {isShowClose && keyGuide !== 'V2_PREVIEW' && (
        <div className="GuideV2Item__Close" onClick={onCloseGuide}>
          <img className="IconClose" src={IconClose} alt="IconClose" width={24} height={24} />
        </div>
      )}
      {!isClickedClose && (
        <>
          <div className="GuideV2Item__Activator" onClick={() => handleToggle(!open)}>
            <img className="icon" src={icon} alt="stepIconUncheck" width={20} height={20} />
            <div className="title">{title}</div>
            {keyGuide !== 'V2_PREVIEW' && (
              <div className="complete">
                {countPassed}/{totalStep} completed
              </div>
            )}
            <div className="collapse">
              {(!isShowClose || keyGuide === 'V2_PREVIEW') && (
                <Icon source={open ? ChevronUpMinor : ChevronDownMinor} tone="base" />
              )}
            </div>
          </div>
          <Collapsible
            open={open}
            id={`basic-collapsible-${keyGuide}`}
            transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}>
            <div className="GuideV2Item__Container">{children}</div>
          </Collapsible>
        </>
      )}
      {keyGuide !== 'V2_PREVIEW' && isClickedClose && !isShowSurveyForm && (
        <>
          <div className="GuideV2Item__Activator" onClick={handleToggle}>
            <img
              className="IconChecked"
              src={IconChecked}
              alt="IconChecked"
              width={20}
              height={20}
            />
            <div className="title">
              {i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_title')}
            </div>
          </div>
          <Collapsible
            open={true}
            id={`basic-collapsible-${keyGuide}`}
            transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}>
            <div className="GuideV2Item__Container">
              <div className="GuideV2Item__Survey">
                <div className="item description">
                  {i18n.translate(
                    'AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_description'
                  )}
                </div>
                <div className="item action">
                  <Button onClick={() => setIsShowSurveyForm(true)}>
                    {i18n.translate(
                      'AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_btn_submit'
                    )}
                  </Button>
                  <span onClick={() => setIsClickedClose(false)}>
                    <u>{i18n.translate('AdminHTML.Common.Action.undo')}</u>
                  </span>
                </div>
              </div>
            </div>
          </Collapsible>
        </>
      )}
      {keyGuide !== 'V2_PREVIEW' && isClickedClose && isShowSurveyForm && (
        <>
          <div className="GuideV2Item__Activator" onClick={handleToggle}>
            <img className="IconChat" src={IconChat} alt="IconChat" width={20} height={20} />
            <div className="title">
              {i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_title2')}
            </div>
          </div>
          <Collapsible
            open={true}
            id={`basic-collapsible-${keyGuide}`}
            transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}>
            <div className="GuideV2Item__Container">
              <div className="GuideV2Item__Survey">
                <div className="item action">
                  <RadioButton
                    label={i18n.translate(
                      'AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_useful'
                    )}
                    id={`${keyGuide}-1`}
                    checked={usefulValue === 1}
                    name={`useful-${keyGuide}`}
                    onChange={() => handleChangeOption(1)}
                  />
                </div>
                <div className="item action">
                  <RadioButton
                    label={i18n.translate(
                      'AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_not_useful'
                    )}
                    id={`${keyGuide}-2`}
                    checked={usefulValue === 2}
                    name={`useful-${keyGuide}`}
                    onChange={() => handleChangeOption(2)}
                  />
                </div>
                <div className="item">
                  <TextField
                    label={i18n.translate(
                      'AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_label'
                    )}
                    value={content}
                    onChange={handleChangeContent}
                    multiline={4}
                    autoComplete="off"
                  />
                </div>
                <div className="item action">
                  <Button
                    disabled={!usefulValue}
                    onClick={handleSubmitFeedback}
                    loading={isLoadingSendFeedback}>
                    {i18n.translate(
                      'AdminHTML.DashboardV3.QuickStartGuideV2.Survey.survey_btn_submit'
                    )}
                  </Button>
                  <span onClick={() => setIsShowSurveyForm(false)}>
                    <u>{i18n.translate('AdminHTML.Common.Action.cancel')}</u>
                  </span>
                </div>
              </div>
            </div>
          </Collapsible>
        </>
      )}
    </div>
  );
}
